/* ============================================================================
 * Hand-drawn SVG charts — exact geometry/colours/behaviour from the CloudCost
 * "Charts & Diagrams" style guide (design/Chart-Style-Guide.dc.html).
 * No chart library. One categorical palette, one grow-in animation (ccgrow,
 * defined in redesign.css), one cursor-following dark tooltip (<ChartTooltip/>).
 * Any element with data-tip / data-tipv triggers the tooltip.
 * ==========================================================================*/
import React, { useEffect } from 'react';

// Shared categorical palette (style-guide order).
export const CHART_PALETTE = [
  '#1C71E3', // primary blue — main series
  '#53A6F0', // light blue — secondary segment
  '#F28E2C', // orange — forecast / attention
  '#16A55C', // green — positive / allocated
  '#7A4FD6', // purple — AI / category
  '#E23D3D', // red — breach / rejected
  '#FFA500', // amber — pending
  '#4E79A7', // muted blue — alt bar series
  '#C6CFDC', // grey — budget line / other
];

// Y-axis ticks — match old MUI charts:
// 1) nice 1/2/5×10^n steps (same idea as MUI continuous auto-scale)
// 2) locale-grouped labels like cost-per-day (integers when step ≥ 1)
function niceNum(range: number, round: boolean): number {
  if (!(range > 0) || !Number.isFinite(range)) return 1;
  const exp = Math.floor(Math.log10(range));
  const frac = range / 10 ** exp;
  let nice: number;
  if (round) {
    if (frac < 1.5) nice = 1;
    else if (frac < 3) nice = 2;
    else if (frac < 7) nice = 5;
    else nice = 10;
  } else if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 5) nice = 5;
  else nice = 10;
  return nice * 10 ** exp;
}

function niceAxis(
  rawMax: number,
  tickCount = 4,
): { max: number; step: number; values: number[] } {
  const positive = Math.max(rawMax, Number.EPSILON);
  let step = niceNum(positive / tickCount, true);
  let n = Math.max(1, Math.ceil(positive / step - 1e-12));
  // Avoid dense axes (e.g. 0, 0.2, …, 1.2) — bump step so we stay near ~tickCount labels.
  if (n > tickCount + 1) {
    step = niceNum(positive / Math.max(2, tickCount - 1), true);
    n = Math.max(1, Math.ceil(positive / step - 1e-12));
  }
  const max = n * step;
  const values = Array.from({ length: n + 1 }, (_, i) => i * step);
  return { max, step, values };
}

function axisLabel(v: number, step: number): string {
  if (v === 0) return '0';
  const maxFrac = step >= 1 ? 0 : step >= 0.1 ? 1 : 2;
  return v.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  });
}

function axisGutter(labels: string[], min = 40): number {
  const widest = Math.max(1, ...labels.map((l) => l.length));
  // ~8px per character at 12px axis font + padding (matches site body labels).
  return Math.max(min, widest * 8 + 14);
}

const esc = (s: unknown) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Shared plot geometry — keep H / pt / pb in sync so side-by-side reporting
// cards (Total Spending + Top 10) share the same baseline height.
export const CHART_H = 420;
export const CHART_PT = 12;
export const CHART_PB = 72; // fits rotated category labels (~32°)

export interface BarSegment {
  v: number;
  c: string;
  l: string;
  tip?: string; // tooltip value override (e.g. full forecast instead of delta)
}
export interface BarDatum {
  v: number;
  l?: string;
  c?: string;
  segments?: BarSegment[]; // stacked segments (bottom-up); v should be their sum
}
export interface DonutDatum {
  v: number; // percent 0-100
  c: string;
  l?: string;
  tv?: string; // tooltip value override
}

// ---- vertical bars / column chart --------------------------------------
function buildVBars(
  data: BarDatum[],
  opts: {
    w?: number;
    h?: number;
    max?: number;
    ticks?: number;
    fmt?: (v: number) => string;
    color?: string;
    refLine?: number;
    refValues?: number[]; // per-band reference line (e.g. monthly budget)
    refLabel?: string;
    rotateLabels?: boolean; // default true; false = horizontal category labels
  } = {},
): string {
  const rotateLabels = opts.rotateLabels !== false;
  const W = opts.w || 560,
    H = opts.h || CHART_H,
    pr = 10,
    pt = CHART_PT,
    // Same deep gutter as the line chart so paired cards share a baseline.
    pb = rotateLabels ? CHART_PB : 40;
  const rawMax = opts.max || Math.max(...data.map((d) => d.v)) * 1.1 || 1;
  const { max, step: tickStep, values: tickValues } = niceAxis(
    rawMax,
    opts.ticks || 4,
  );
  const tickLabels = tickValues.map((gv) => axisLabel(gv, tickStep));
  const pl = axisGutter(tickLabels, 48);
  const iw = W - pl - pr,
    ih = H - pt - pb;
  const n = data.length || 1,
    gap = 0.36,
    bw = (iw / n) * (1 - gap),
    step = iw / n;
  let grid = '';
  tickValues.forEach((gv, ti) => {
    const gy = pt + ih - (gv / max) * ih;
    grid += `<line x1="${pl}" y1="${gy}" x2="${W - pr}" y2="${gy}" stroke="#EEF1F6"/><text x="${pl - 7}" y="${gy + 4}" font-size="12" fill="#9BA6B7" text-anchor="end">${esc(tickLabels[ti])}</text>`;
  });
  let bars = '';
  data.forEach((d, i) => {
    const bh = (d.v / max) * ih,
      bx = pl + step * i + (step - bw) / 2,
      by = pt + ih - bh;
    const cx = bx + bw / 2;
    if (d.segments) {
      const segs = d.segments.filter((s) => s.v > 0);
      if (segs.length) {
        let rects = '',
          yTop = pt + ih;
        segs.forEach((s, si) => {
          const sh = (s.v / max) * ih;
          yTop -= sh;
          const tip = `${d.l || ''} · ${s.l}`;
          const tipv = s.tip ?? (opts.fmt ? opts.fmt(s.v) : String(s.v));
          if (si === segs.length - 1) {
            const r = Math.min(4, sh / 2, bw / 2);
            rects += `<path d="M${bx.toFixed(1)} ${(yTop + sh).toFixed(1)} V${(yTop + r).toFixed(1)} Q${bx.toFixed(1)} ${yTop.toFixed(1)} ${(bx + r).toFixed(1)} ${yTop.toFixed(1)} H${(bx + bw - r).toFixed(1)} Q${(bx + bw).toFixed(1)} ${yTop.toFixed(1)} ${(bx + bw).toFixed(1)} ${(yTop + r).toFixed(1)} V${(yTop + sh).toFixed(1)} Z" fill="${s.c}" data-tip="${esc(tip)}" data-tipv="${esc(tipv)}"/>`;
          } else {
            rects += `<rect x="${bx.toFixed(1)}" y="${yTop.toFixed(1)}" width="${bw.toFixed(1)}" height="${sh.toFixed(1)}" fill="${s.c}" data-tip="${esc(tip)}" data-tipv="${esc(tipv)}"/>`;
          }
        });
        bars += `<g style="transform-origin:${cx.toFixed(1)}px ${(pt + ih).toFixed(1)}px;animation:ccgrow .5s ease both">${rects}</g>`;
      }
    } else {
      const col = d.c || opts.color || '#1C71E3';
      bars += `<rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, bh).toFixed(1)}" rx="4" fill="${col}" data-tip="${esc(d.l || '')}" data-tipv="${esc(opts.fmt ? opts.fmt(d.v) : d.v)}" style="transform-origin:${cx.toFixed(1)}px ${(pt + ih).toFixed(1)}px;animation:ccgrow .5s ease both"/>`;
    }
    const lab = (d.l || '').length > 11 ? d.l!.slice(0, 10) + '…' : d.l || '';
    // Anchor just under the axis; remaining pb clears the rotated glyph extent.
    const labelY = pt + ih + (rotateLabels ? 14 : 18);
    if (!rotateLabels) {
      bars += `<text x="${cx.toFixed(1)}" y="${labelY}" font-size="12" fill="#7A8497" text-anchor="middle">${esc(lab)}</text>`;
    } else {
      bars += `<text x="${cx.toFixed(1)}" y="${labelY}" font-size="12" fill="#7A8497" text-anchor="end" transform="rotate(-32 ${cx.toFixed(1)} ${labelY})">${esc(lab)}</text>`;
    }
  });
  let ref = '';
  if (opts.refValues && opts.refValues.some((v) => v > 0)) {
    const pts = opts.refValues.slice(0, n).map((v, i) => ({
      x: pl + step * i + step / 2,
      y: pt + ih - (Math.min(v, max) / max) * ih,
      v,
    }));
    ref += `<polyline points="${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}" fill="none" stroke="#E23D3D" stroke-width="1.5" stroke-dasharray="5 4"/>`;
    pts.forEach((p, i) => {
      const bandLabel = (data[i] && data[i].l) || '';
      const tip = (bandLabel ? bandLabel + ' · ' : '') + (opts.refLabel || '');
      ref += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="9" fill="#E23D3D" fill-opacity="0" pointer-events="all" data-tip="${esc(tip)}" data-tipv="${esc(opts.fmt ? opts.fmt(p.v) : p.v)}"/><circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.5" fill="#E23D3D" pointer-events="none"/>`;
    });
    if (opts.refLabel) {
      const last = pts[pts.length - 1];
      ref += `<text x="${W - pr}" y="${(last.y - 7).toFixed(1)}" font-size="12" font-weight="700" fill="#E23D3D" text-anchor="end">${esc(opts.refLabel)}</text>`;
    }
  } else if (opts.refLine) {
    const ry = pt + ih - (opts.refLine / max) * ih;
    ref = `<line x1="${pl}" y1="${ry}" x2="${W - pr}" y2="${ry}" stroke="#E23D3D" stroke-width="1.5" stroke-dasharray="5 4"/><text x="${W - pr}" y="${ry - 5}" font-size="12" font-weight="700" fill="#E23D3D" text-anchor="end">${esc(opts.refLabel || '')}</text>`;
  }
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block">${grid}${bars}${ref}</svg>`;
}

// ---- donut / ring -------------------------------------------------------
// `size` scales the whole ring proportionally (style-guide base = 164; ring
// stroke and centre labels keep the same ratios) so the donut can fill wider
// cards instead of floating tiny in the middle.
function buildDonut(
  data: DonutDatum[],
  centerTop?: string,
  centerBot?: string,
  size = 164,
): string {
  const cx = size / 2,
    cy = size / 2,
    r = size * 0.366,
    sw = size * 0.134,
    C = 2 * Math.PI * r;
  // Scale with size for small donuts, but cap so large rings (e.g. reporting
  // ~400px) don't blow up the centre "Total" / amount labels. Long currency
  // strings shrink further so they stay inside the hole.
  const topFont = Math.min(13, Math.round(size * 0.073 * 10) / 10);
  const holeW = size * 0.52;
  let botFont = Math.min(18, Math.round(size * 0.11 * 10) / 10);
  if (centerBot && centerBot.length > 0) {
    const fit = holeW / (centerBot.length * 0.62);
    botFont = Math.min(botFont, Math.max(11, Math.round(fit * 10) / 10));
  }
  let off = 0,
    arcs = '';
  data.forEach((d) => {
    const len = (d.v / 100) * C;
    arcs += `<circle cx="${cx}" cy="${cy}" r="${r.toFixed(1)}" fill="none" stroke="${d.c}" stroke-width="${sw.toFixed(1)}" stroke-dasharray="${len.toFixed(2)} ${(C - len).toFixed(2)}" stroke-dashoffset="${(-off).toFixed(2)}" transform="rotate(-90 ${cx} ${cy})" data-tip="${esc(d.l || '')}" data-tipv="${esc(d.tv || d.v + '%')}" style="cursor:default"/>`;
    off += len;
  });
  // width/height 100% + viewBox so the ring scales with its CSS box
  // (fixed px broke half-width reporting cards).
  return `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" style="display:block">${arcs}<text x="${cx}" y="${(cy - size * 0.03).toFixed(1)}" font-size="${topFont}" fill="#8A94A6" text-anchor="middle" font-weight="700">${esc(centerTop || '')}</text><text x="${cx}" y="${(cy + size * 0.098).toFixed(1)}" font-size="${botFont}" fill="#16233A" text-anchor="middle" font-weight="900">${esc(centerBot || '')}</text></svg>`;
}

// ---- horizontal bars with track ----------------------------------------
function buildHBars(
  data: BarDatum[],
  opts: { w?: number; labelW?: number; fmt?: (v: number) => string; color?: string } = {},
): string {
  const rowH = 38,
    W = opts.w || 520,
    pl = 4,
    pr = 14,
    labelW = opts.labelW || 150;
  const valW = 70,
    barMax = W - pl - labelW - valW - 12 - pr,
    max = Math.max(...data.map((d) => d.v)) || 1;
  let rows = '';
  data.forEach((d, i) => {
    const bw = (d.v / max) * barMax,
      y = i * rowH;
    rows += `<g transform="translate(0 ${y})"><text x="${pl}" y="${rowH / 2 + 4}" font-size="12.5" fill="#3D4A5C" font-weight="700">${esc(d.l)}</text><rect x="${labelW}" y="${rowH / 2 - 8}" width="${barMax}" height="16" rx="8" fill="#F1F4F9"/><rect x="${labelW}" y="${rowH / 2 - 8}" width="${Math.max(4, bw).toFixed(1)}" height="16" rx="8" fill="${d.c || opts.color || '#1C71E3'}" data-tip="${esc(d.l || '')}" data-tipv="${esc(opts.fmt ? opts.fmt(d.v) : d.v)}" style="transform-origin:${labelW}px 0;animation:ccgrow .5s ease both"/><text x="${W - pr}" y="${rowH / 2 + 4}" font-size="12.5" fill="#16233A" font-weight="900" text-anchor="end">${esc(opts.fmt ? opts.fmt(d.v) : d.v)}</text></g>`;
  });
  return `<svg viewBox="0 0 ${W} ${data.length * rowH}" width="100%" height="${data.length * rowH}" style="display:block">${rows}</svg>`;
}

// ---- trend line + area + forecast + budget -----------------------------
const LINE_SERIES_COLORS = {
  actual: '#1C71E3',
  lastMonth: '#4E79A7',
  forecast: '#F28E2C',
  budget: '#E23D3D',
} as const;

export interface LineChartSeriesLabels {
  actual?: string;
  lastMonth?: string;
  forecast?: string;
  budget?: string;
}

export interface LineChartOpts {
  actual: number[];
  forecast?: number[]; // continues from the last actual point
  // forecast[0] duplicates the last actual point and is skipped as a hover
  // datapoint (default true); pass false when forecast[0] is a real value at
  // the last actual x-slot.
  forecastOverlap?: boolean;
  lastMonth?: (number | null)[]; // full-month overlay, indexed from slot 0
  budgetLine?: number;
  max?: number;
  totalSlots?: number; // x-axis divisions (default 15)
  w?: number; // viewBox width; when omitted SvgLineChart measures the card
  h?: number; // viewBox height (default CHART_H — matches bar charts)
  pb?: number; // bottom gutter (default CHART_PB — matches bar charts)
  fmt?: (v: number) => string;
  xLabels?: string[]; // evenly spaced axis labels
  pointLabels?: string[]; // per-point labels (e.g. dates) for hover tooltips
  budgetLabel?: string;
  seriesLabels?: LineChartSeriesLabels;
  hoverIndex?: number | null;
}

export interface LineChartTipRow {
  color: string;
  label: string;
  value: string;
  y: number | null; // plot y for marker; null = no marker
}

function lineChartLayout(opts: LineChartOpts) {
  const W = opts.w || 640,
    H = opts.h || CHART_H,
    pr = 12,
    pt = CHART_PT,
    pb = opts.pb ?? CHART_PB;
  const actual = opts.actual || [];
  const forecast = opts.forecast || [];
  const lastMonth = opts.lastMonth || [];
  const slots = opts.totalSlots || 15;
  const allVals = [
    ...actual,
    ...forecast,
    ...(lastMonth.filter((v) => v != null) as number[]),
    opts.budgetLine || 0,
  ].filter((v) => v != null);
  const rawMax = opts.max || Math.max(...allVals, 1) * 1.05;
  const { max: axisMax, step: tickStep, values: tickValues } = niceAxis(
    rawMax,
    4,
  );
  const tickLabels = tickValues.map((gv) => axisLabel(gv, tickStep));
  const pl = axisGutter(tickLabels, 54);
  const iw = W - pl - pr,
    ih = H - pt - pb;
  const y = (v: number) => pt + ih - (v / axisMax) * ih;
  const x = (i: number) => pl + (iw * i) / slots;
  const days = actual.length;
  const pointCount = slots + 1;
  return {
    W,
    H,
    pl,
    pr,
    pt,
    pb,
    iw,
    ih,
    slots,
    pointCount,
    axisMax,
    tickStep,
    tickValues,
    tickLabels,
    actual,
    forecast,
    lastMonth,
    days,
    x,
    y,
  };
}

function lineChartTipRows(
  opts: LineChartOpts,
  index: number,
  layout: ReturnType<typeof lineChartLayout>,
): LineChartTipRow[] {
  const { actual, forecast, lastMonth, days, y } = layout;
  const fmt = opts.fmt || ((v: number) => String(Math.round(v)));
  const labels = opts.seriesLabels || {};
  const actualLabel = labels.actual || 'Current Month';
  const lastMonthLabel = labels.lastMonth || 'Last Month';
  const forecastLabel = labels.forecast || 'Forecast';
  const budgetLabel =
    labels.budget || opts.budgetLabel || 'Budget';
  const noData = 'No data';
  const rows: LineChartTipRow[] = [];

  const actualVal = index >= 0 && index < actual.length ? actual[index] : null;
  rows.push({
    color: LINE_SERIES_COLORS.actual,
    label: actualLabel,
    value: actualVal == null ? noData : fmt(actualVal),
    y: actualVal == null ? null : y(actualVal),
  });

  if (lastMonth.some((v) => v != null)) {
    const lmVal =
      index >= 0 && index < lastMonth.length ? lastMonth[index] : null;
    rows.push({
      color: LINE_SERIES_COLORS.lastMonth,
      label: lastMonthLabel,
      value: lmVal == null ? noData : fmt(lmVal),
      y: lmVal == null ? null : y(lmVal),
    });
  }

  if (forecast.length > 0) {
    // forecast[0] sits on the last actual x-slot; later points continue forward.
    const forecastStart = Math.max(0, days - 1);
    const fi = index - forecastStart;
    const fVal = fi >= 0 && fi < forecast.length ? forecast[fi] : null;
    rows.push({
      color: LINE_SERIES_COLORS.forecast,
      label: forecastLabel,
      value: fVal == null ? noData : fmt(fVal),
      y: fVal == null ? null : y(fVal),
    });
  }

  if (opts.budgetLine != null && opts.budgetLine > 0) {
    rows.push({
      color: LINE_SERIES_COLORS.budget,
      label: budgetLabel,
      value: fmt(opts.budgetLine),
      y: y(opts.budgetLine),
    });
  }

  return rows;
}

function buildLineChart(opts: LineChartOpts): string {
  const layout = lineChartLayout(opts);
  const {
    W,
    H,
    pl,
    pr,
    pt,
    pb,
    iw,
    ih,
    actual,
    forecast,
    lastMonth,
    days,
    tickValues,
    tickLabels,
    x,
    y,
  } = layout;
  const fmt = opts.fmt || ((v: number) => String(Math.round(v)));

  let ap = '',
    fp = '';
  actual.forEach((v, i) => {
    ap += (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1) + ' ';
  });
  forecast.forEach((v, i) => {
    fp += (i ? 'L' : 'M') + x(days - 1 + i).toFixed(1) + ' ' + y(v).toFixed(1) + ' ';
  });
  let lp = '';
  let lpGap = true;
  lastMonth.forEach((v, i) => {
    if (v == null) {
      lpGap = true;
      return;
    }
    lp += (lpGap ? 'M' : 'L') + x(i).toFixed(1) + ' ' + y(v).toFixed(1) + ' ';
    lpGap = false;
  });
  const lm = lp
    ? `<path d="${lp}" fill="none" stroke="${LINE_SERIES_COLORS.lastMonth}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
    : '';
  const area = days
    ? ap + 'L' + x(days - 1).toFixed(1) + ' ' + (pt + ih) + ' L' + x(0).toFixed(1) + ' ' + (pt + ih) + ' Z'
    : '';

  let grid = '';
  tickValues.forEach((gv, ti) => {
    const gy = y(gv);
    grid += `<line x1="${pl}" y1="${gy}" x2="${W - pr}" y2="${gy}" stroke="#EEF1F6" stroke-width="1"/><text x="${pl - 8}" y="${gy + 4}" font-size="12" fill="#9BA6B7" text-anchor="end">${esc(tickLabels[ti])}</text>`;
  });
  let xlabels = '';
  const xl = opts.xLabels || [];
  xl.forEach((l, i) => {
    const xx = pl + (iw * i) / Math.max(1, xl.length - 1);
    // Sit just under the axis (same band as bar-chart category labels).
    if (xx <= W - pr) xlabels += `<text x="${xx}" y="${pt + ih + 18}" font-size="12" fill="#9BA6B7" text-anchor="middle">${esc(l)}</text>`;
  });

  let budget = '';
  if (opts.budgetLine) {
    const by = y(opts.budgetLine);
    // Solid red budget line to match the cost-per-day reference (old MUI chart).
    budget = `<line x1="${pl}" y1="${by}" x2="${W - pr}" y2="${by}" stroke="${LINE_SERIES_COLORS.budget}" stroke-width="1.5"/><text x="${W - pr}" y="${by - 6}" font-size="12" font-weight="700" fill="${LINE_SERIES_COLORS.budget}" text-anchor="end">${esc(opts.budgetLabel || 'Budget ' + fmt(opts.budgetLine))}</text>`;
  }

  // Decorative marks on the actual series (markers for hover are drawn in the crosshair).
  let dots = '';
  actual.forEach((v, i) => {
    dots += `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="3" fill="${LINE_SERIES_COLORS.actual}" pointer-events="none"/>`;
  });

  let crosshair = '';
  if (opts.hoverIndex != null && opts.hoverIndex >= 0) {
    const hi = opts.hoverIndex;
    const cx = x(hi);
    crosshair += `<line x1="${cx.toFixed(1)}" y1="${pt}" x2="${cx.toFixed(1)}" y2="${(pt + ih).toFixed(1)}" stroke="#16233A" stroke-width="1" stroke-dasharray="4 3" pointer-events="none"/>`;
    const tipRows = lineChartTipRows(opts, hi, layout);
    tipRows.forEach((row) => {
      if (row.y == null) return;
      crosshair += `<circle cx="${cx.toFixed(1)}" cy="${row.y.toFixed(1)}" r="4.5" fill="${row.color}" stroke="#fff" stroke-width="1.5" pointer-events="none"/>`;
    });
  }

  // Invisible full-height hit band so the whole plot area is hoverable.
  const hit = `<rect x="${pl}" y="${pt}" width="${iw}" height="${ih}" fill="transparent" pointer-events="all"/>`;

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block"><defs><linearGradient id="cclg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${LINE_SERIES_COLORS.actual}" stop-opacity="0.18"/><stop offset="1" stop-color="${LINE_SERIES_COLORS.actual}" stop-opacity="0"/></linearGradient></defs>${grid}${budget}${lm}<path d="${area}" fill="url(#cclg)"/><path d="${fp}" fill="none" stroke="${LINE_SERIES_COLORS.forecast}" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round"/><path d="${ap}" fill="none" stroke="${LINE_SERIES_COLORS.actual}" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"/>${xlabels}${dots}${crosshair}${hit}</svg>`;
}

// ---- React wrappers -----------------------------------------------------
function Svg({ html, style }: { html: string; style?: React.CSSProperties }) {
  return (
    <div
      style={{ width: '100%', display: 'flex', justifyContent: 'center', ...style }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function SvgVBars(props: Parameters<typeof buildVBars>[1] & { data: BarDatum[] }) {
  const { data, ...opts } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const [measuredW, setMeasuredW] = React.useState<number | null>(null);

  // When callers omit `w`, measure the card so split charts fill the viewport
  // (same approach as SvgHBars / monthly consumption charts).
  React.useLayoutEffect(() => {
    if (opts.w != null) return;
    const el = ref.current;
    if (!el) return;
    const measure = () => setMeasuredW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [opts.w]);

  const width = opts.w ?? (measuredW != null && measuredW > 0 ? Math.max(360, measuredW) : null);

  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width != null && <Svg html={buildVBars(data, { ...opts, w: width })} />}
    </div>
  );
}
// Measures its container so viewBox units render 1:1 with CSS pixels — rows
// stay 38px tall and bars 16px thick at any card width.
export function SvgHBars(props: Parameters<typeof buildHBars>[1] & { data: BarDatum[] }) {
  const { data, ...opts } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const [w, setW] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {w != null && w > 0 && <Svg html={buildHBars(data, { ...opts, w: Math.max(360, w) })} />}
    </div>
  );
}
export function SvgDonut({
  data,
  centerTop,
  centerBot,
  size = 164,
}: {
  data: DonutDatum[];
  centerTop?: string;
  centerBot?: string;
  size?: number;
}) {
  // Cap at `size`, but shrink with the parent so half-width cards don't overflow.
  return (
    <Svg
      html={buildDonut(data, centerTop, centerBot, size)}
      style={{
        width: '100%',
        maxWidth: size,
        aspectRatio: '1',
        flex: 'none',
      }}
    />
  );
}
export function SvgLineChart(props: LineChartOpts) {
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [measuredW, setMeasuredW] = React.useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);
  const [tipPos, setTipPos] = React.useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });

  // Measure card width so viewBox units = CSS pixels (axis labels stay ~12px
  // instead of upscaling with width="100%" on a fixed 640 viewBox).
  React.useLayoutEffect(() => {
    if (props.w != null) return;
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setMeasuredW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [props.w]);

  const chartW =
    props.w ??
    (measuredW != null && measuredW > 0 ? Math.max(360, measuredW) : null);

  const layout = React.useMemo(
    () =>
      lineChartLayout({
        actual: props.actual,
        forecast: props.forecast,
        lastMonth: props.lastMonth,
        budgetLine: props.budgetLine,
        max: props.max,
        totalSlots: props.totalSlots,
        w: chartW ?? 640,
        h: props.h,
      }),
    [
      props.actual,
      props.forecast,
      props.lastMonth,
      props.budgetLine,
      props.max,
      props.totalSlots,
      props.h,
      chartW,
    ],
  );

  const tipRows =
    hoverIndex != null ? lineChartTipRows(props, hoverIndex, layout) : [];
  const tipTitle =
    hoverIndex != null
      ? (props.pointLabels && props.pointLabels[hoverIndex]) || ''
      : '';

  const resolveIndex = (clientX: number, clientY: number): number | null => {
    const svg = wrapRef.current?.querySelector('svg');
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      return null;
    }
    const vb = svg.viewBox.baseVal;
    const scaleX = vb.width / rect.width;
    const mx = (clientX - rect.left) * scaleX;
    if (mx < layout.pl || mx > layout.W - layout.pr) return null;
    const raw = ((mx - layout.pl) / layout.iw) * layout.slots;
    return Math.max(0, Math.min(layout.pointCount - 1, Math.round(raw)));
  };

  const onMouseMove = (e: React.MouseEvent) => {
    const idx = resolveIndex(e.clientX, e.clientY);
    setHoverIndex((prev) => (prev === idx ? prev : idx));
    if (idx == null) return;
    // Place the white multi-series tip near the cursor, flipped if near edges.
    const tipW = 220;
    const tipH = 140;
    let left = e.clientX + 16;
    let top = e.clientY + 16;
    if (left + tipW > window.innerWidth - 8) left = e.clientX - tipW - 16;
    if (top + tipH > window.innerHeight - 8) top = e.clientY - tipH - 16;
    setTipPos({ left, top });
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', width: '100%' }}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setHoverIndex(null)}>
      {chartW != null && (
        <Svg html={buildLineChart({ ...props, w: chartW, hoverIndex })} />
      )}
      {hoverIndex != null && tipRows.length > 0 && (
        <div
          style={{
            position: 'fixed',
            left: tipPos.left,
            top: tipPos.top,
            zIndex: 2000,
            pointerEvents: 'none',
            background: '#fff',
            color: '#16233A',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #E5EAF0',
            boxShadow: '0 8px 24px rgba(16,35,58,.14)',
            fontFamily: 'Lato,sans-serif',
            minWidth: 168,
          }}>
          {tipTitle ? (
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                marginBottom: 8,
                color: '#16233A',
              }}>
              {tipTitle}
            </div>
          ) : null}
          {tipRows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4,
                fontSize: 12,
                fontWeight: 700,
              }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: row.color,
                  flex: 'none',
                }}
              />
              <span style={{ flex: 1, color: '#3D4A5C' }}>{row.label}</span>
              <span style={{ color: '#16233A', fontWeight: 900 }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- shared legend rows (donut companion) ------------------------------
export function ChartLegend({
  items,
  direction = 'column',
  compact = false,
}: {
  items: { color: string; name: string; value?: string }[];
  direction?: 'column' | 'row';
  compact?: boolean;
}) {
  const row = direction === 'row';
  const fontSize = compact ? 12 : 14;
  const swatch = compact ? 8 : 10;
  return (
    <div
      style={
        row
          ? { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: compact ? '6px 14px' : '8px 22px', width: '100%' }
          : { display: 'flex', flexDirection: 'column', gap: compact ? 8 : 11, width: '100%' }
      }>
      {items.map((b, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 10 }}>
          <span
            style={{ width: swatch, height: swatch, borderRadius: 3, background: b.color, flex: 'none' }}
          />
          <span
            style={{
              flex: row ? 'none' : 1,
              fontSize,
              color: '#3D4A5C',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {b.name}
          </span>
          {b.value != null && (
            <span style={{ fontSize, color: '#16233A', fontWeight: 900, flex: 'none' }}>
              {b.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- one global cursor-following tooltip (mount once in the shell) ------
export function ChartTooltip() {
  useEffect(() => {
    if (!document.getElementById('csg-tip')) {
      const d = document.createElement('div');
      d.id = 'csg-tip';
      d.style.cssText =
        'position:fixed;left:0;top:0;z-index:2000;pointer-events:none;opacity:0;transition:opacity .1s;background:#16233A;color:#fff;padding:7px 11px;border-radius:8px;box-shadow:0 8px 20px rgba(16,35,58,.28);font-family:Lato,sans-serif';
      d.innerHTML =
        '<div class="l" style="opacity:.72;font-weight:700;font-size:11px;margin-bottom:1px"></div><div class="v" style="font-size:14px;font-weight:900"></div>';
      document.body.appendChild(d);
    }
    const handler = (e: MouseEvent) => {
      const t = document.getElementById('csg-tip');
      if (!t) return;
      const target = e.target as Element | null;
      const el = target && target.closest ? target.closest('[data-tip]') : null;
      if (el) {
        (t.querySelector('.l') as HTMLElement).textContent = el.getAttribute('data-tip') || '';
        (t.querySelector('.v') as HTMLElement).textContent = el.getAttribute('data-tipv') || '';
        t.style.opacity = '1';
        const r = t.getBoundingClientRect();
        let x = e.clientX + 14,
          y = e.clientY + 14;
        if (x + r.width > window.innerWidth) x = e.clientX - r.width - 14;
        if (y + r.height > window.innerHeight) y = e.clientY - r.height - 14;
        t.style.left = x + 'px';
        t.style.top = y + 'px';
      } else {
        t.style.opacity = '0';
      }
    };
    document.addEventListener('mousemove', handler);
    return () => document.removeEventListener('mousemove', handler);
  }, []);
  return null;
}
